/**
 * Origem da compra: registra de qual seção da loja saiu cada produto que entrou na sacola e grava no
 * atributo do carrinho "Origem da compra". A EvolveHub leva os atributos do carrinho para o pedido
 * (note_attributes), então aparece nos detalhes adicionais do pedido na Shopify.
 *
 * Como funciona, sem depender de cada seção:
 * - Todo clique dentro de uma seção (.shopify-section) vira a "origem atual" (página › título da seção).
 *   Cliques fora de seções (quick view, gaveta aberta por cima etc.) mantêm a origem do clique que abriu.
 * - Clique em link de produto guarda a origem para aquele produto: quando a compra acontece na página do
 *   produto, fica "Página do produto ← <seção de onde veio>".
 * - Depois de qualquer chamada de carrinho (fetch) e a cada carregamento de página, compara a sacola com o
 *   que já foi registrado: variante nova recebe a origem atual. Isso cobre fetch, formulário e /cart/add?id=.
 * Configuração da tag em layout/theme.liquid (data-page, data-title, data-items).
 */
(() => {
  if (window.__bzOrigem) return;
  window.__bzOrigem = true;

  const me = document.currentScript || document.querySelector('script[data-bz-origem]');
  const PAGE = (me && me.dataset.page) || '';
  const TITLE = (me && me.dataset.title) || '';
  const ITEMS = Number((me && me.dataset.items) || 0);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const ATTR = 'Origem da compra';
  const K_MAP = 'bz_origem_itens'; // { variantId: { t: título, o: origem } }
  const K_NOW = 'bz_origem_atual'; // { o: origem, at: timestamp }
  const K_PROD = 'bz_origem_produtos'; // { handle: origem }
  const MAX_AGE = 30 * 60 * 1000; // origem do último clique vale por 30 min
  const nativeFetch = window.fetch.bind(window);

  const read = (key, area = localStorage) => {
    try {
      return JSON.parse(area.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  };
  const write = (key, value, area = localStorage) => {
    try {
      area.setItem(key, JSON.stringify(value));
    } catch (error) {}
  };

  const clean = (text) => (text || '').replace(/\s+/g, ' ').trim().slice(0, 70);

  const pageLabel = () => {
    if (PAGE === 'index') return 'Home';
    if (PAGE === 'product') return `Produto ${TITLE}`;
    if (PAGE === 'collection') return `Coleção ${TITLE}`;
    if (PAGE === 'page') return `Página ${TITLE}`;
    if (PAGE === 'search') return 'Busca';
    if (PAGE === 'cart') return 'Carrinho';
    if (PAGE === 'blog' || PAGE === 'article') return 'Blog';
    return PAGE || 'Loja';
  };

  const FIXED = { header: 'Cabeçalho', footer: 'Rodapé', 'announcement-bar': 'Barra de anúncio', 'cart-drawer': 'Sacola' };

  const sectionLabel = (section) => {
    const id = section.id.replace('shopify-section-', '');
    const key = id.split('__').pop();
    if (FIXED[key]) return FIXED[key];
    if (PAGE === 'product' && key === 'main') return 'Página do produto';
    const heading = section.querySelector('h1, h2, h3');
    return clean(heading && heading.textContent) || key.replace(/_[A-Za-z0-9]{6}$/, '').replace(/[_-]+/g, ' ');
  };

  // Seção de um elemento. Barras que o tema move para o <body> guardam a seção de origem em data-bz-from.
  const sectionOf = (element) => {
    const moved = element.closest('[data-bz-from]');
    if (moved) return document.getElementById(moved.dataset.bzFrom);
    return element.closest('.shopify-section');
  };

  const originOf = (section) => {
    const label = sectionLabel(section);
    if (label === 'Página do produto') {
      const handle = (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
      const from = handle && (read(K_PROD, sessionStorage) || {})[decodeURIComponent(handle)];
      return `Página do produto ← ${from || 'acesso direto'}`;
    }
    if (Object.values(FIXED).includes(label)) return label;
    return `${pageLabel()} › ${label}`;
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const section = sectionOf(event.target);
      if (!section) {
        // Overlay fora das seções (quick view): mantém a origem, só renova o prazo
        const now = read(K_NOW, sessionStorage);
        if (now) write(K_NOW, { o: now.o, at: Date.now() }, sessionStorage);
        return;
      }
      const origin = originOf(section);
      write(K_NOW, { o: origin, at: Date.now() }, sessionStorage);

      const link = event.target.closest('a[href*="/products/"]');
      const handle = link && (link.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
      if (handle) {
        const products = read(K_PROD, sessionStorage) || {};
        products[decodeURIComponent(handle)] = origin.replace(/^Página do produto ← /, '');
        write(K_PROD, products, sessionStorage);
      }
    },
    true
  );

  let syncing = null;
  const sync = () => {
    if (syncing) return syncing;
    syncing = (async () => {
      const response = await nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const map = read(K_MAP) || {};
      const now = read(K_NOW, sessionStorage);
      const current = now && Date.now() - now.at < MAX_AGE ? now.o : 'não identificada';
      const next = {};
      cart.items.forEach((item) => {
        const id = String(item.variant_id);
        if (next[id]) return;
        next[id] = map[id] || { t: item.product_title, o: current };
      });
      write(K_MAP, next);

      const value = Object.values(next)
        .map((entry) => `${entry.t}: ${entry.o}`)
        .join(' | ');
      if ((cart.attributes || {})[ATTR] === value || (!value && !(cart.attributes || {})[ATTR])) return;
      await nativeFetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [ATTR]: value } }),
      });
    })()
      .catch(() => {})
      .finally(() => {
        syncing = null;
      });
    return syncing;
  };

  // Depois de qualquer alteração de sacola feita pelo tema (add, change, clear), sincroniza
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const result = nativeFetch(input, init);
    if (/\/cart\/(add|change|clear)/.test(url)) {
      result.then(() => setTimeout(sync, 50)).catch(() => {});
    }
    return result;
  };

  // Adições que trocam de página (/cart/add?id=, formulário sem JS) aparecem no próximo carregamento
  if (ITEMS > 0 || Object.keys(read(K_MAP) || {}).length) sync();
})();
